import { DETAIL, DETAIL_LEVELS, detailAt, detailIndex } from '../render/detail';
import type { RenderSettings } from '../render/renderSettings';

// The Settings panel (D187, D190) — the Graphics tier as a slider, plus the fps toggle.
//
// It exists because the decimation ratio is a LOOKING question: `tools/models/lods.mjs`
// writes every tier of the ladder, and this is where you choose one and go and look at the
// board. The fps READOUT that used to sit beside the slider is gone (D190): on the main menu
// it could only ever report the menu's own idle rate, which answers nothing. It is a toggle
// now, and the counter it raises lives on the board where the frames actually are.
//
// A stage cannot absorb a new tier (geometry loads once, the shadow map is configured once), so
// since D191 a change REBUILDS a board in play, keeping the camera, and the note under the slider
// says so. The panel opens from the main menu and from the in-game pause menu alike.
//
// House rules: injected document, every string via textContent (T-SEC-006), no default export.

export interface SettingsPanelOpts {
  doc: Document;
  /** Current values, read on open so the panel never shows a stale tier. */
  read(): RenderSettings;
  /** Persist + apply. The caller decides whether that also means "rebuild the board". */
  write(next: RenderSettings): void;
  /**
   * D210 (the user's ask): the master mute lives here too — a third instance of the SAME
   * store the menu bar and the break menu toggle, kept in sync through onMuteChange.
   * Omitted ⇒ no sound row (headless suites, pre-audio embedders).
   */
  /**
   * D212: the anonymous-usage toggle (privacy page's "Settings → Share anonymous usage
   * data"). Omitted ⇒ no row — headless suites and pre-telemetry embedders unchanged.
   */
  usage?: { get(): boolean; set(v: boolean): void };
  /**
   * D215: hidden-tab notifications. `state` folds the browser's permission in:
   * unsupported ⇒ no row at all; denied ⇒ the honest "blocked in the browser" note;
   * default ⇒ an Enable button (the required user gesture); granted ⇒ a plain toggle.
   */
  notify?: { state(): 'unsupported' | 'denied' | 'default' | 'granted'; request(): Promise<void>; get(): boolean; set(v: boolean): void };
  audio?: {
    isMuted(): boolean;
    setMuted(muted: boolean): void;
    onMuteChange(handler: () => void): () => void;
  };
}

export interface SettingsPanel {
  readonly root: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  dispose(): void;
}

const ROW = 'display:flex;align-items:center;justify-content:space-between;gap:16px;padding:9px 0;border-top:1px solid var(--hud-border-pale,rgba(0,0,0,.14))';

export function mountSettingsPanel(opts: SettingsPanelOpts): SettingsPanel {
  const d = opts.doc;
  const root = d.createElement('div');
  root.className = 'settings-scrim';
  root.dataset['settingsPanel'] = '1';
  root.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;' +
    'background:rgba(8,38,46,.72);z-index:240;padding:20px;font-family:var(--font-hud,system-ui)';
  // D210: the panel mounts on <body>, outside the zoomed HUD bands — scale it the same way
  root.style.setProperty('zoom', 'var(--hud-zoom, 1)');

  const panel = d.createElement('div');
  panel.style.cssText =
    'width:min(460px,96vw);max-height:88vh;overflow-y:auto;background:var(--hud-cream,#f7f1e3);' +
    'border:3px solid var(--hud-border,#2b2019);border-radius:18px;padding:20px 22px;color:var(--hud-ink,#2b2019)';
  root.append(panel);

  const title = d.createElement('h2');
  title.textContent = 'Settings';
  title.style.cssText = 'margin:0 0 14px;font-size:22px;font-weight:700';
  panel.append(title);

  const toggle = (labelText: string, hint: string, key: 'fps' | 'liveBoard' | 'props' | 'stats'): HTMLButtonElement => {
    const row = d.createElement('div');
    row.style.cssText = ROW;
    const text = d.createElement('div');
    const t = d.createElement('div');
    t.textContent = labelText;
    t.style.cssText = 'font-weight:600;font-size:13px';
    const h = d.createElement('div');
    h.textContent = hint;
    h.style.cssText = 'font-size:11.5px;opacity:.68;margin-top:1px;line-height:1.35';
    text.append(t, h);
    const b = d.createElement('button');
    b.type = 'button';
    b.dataset['debugToggle'] = key;
    b.style.cssText =
      'flex:0 0 auto;min-width:56px;padding:6px 12px;border-radius:999px;cursor:pointer;font:inherit;' +
      'font-size:12px;font-weight:700;border:2px solid var(--hud-border,#2b2019)';
    b.addEventListener('click', () => set({ ...state, [key]: !state[key] }));
    row.append(text, b);
    panel.append(row);
    return b;
  };

  // ── Graphics ─────────────────────────────────────────────────────────────────
  const label = d.createElement('div');
  label.textContent = 'Graphics';
  label.style.cssText = 'font-size:15px;font-weight:700';
  panel.append(label);

  const slider = d.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = String(DETAIL_LEVELS.length - 1);
  slider.step = '1';
  slider.dataset['graphicsSlider'] = '1';
  slider.setAttribute('aria-label', 'Graphics');
  slider.style.cssText = 'width:100%;margin:12px 0 2px;accent-color:var(--hud-green,#3d8a58);cursor:pointer';
  panel.append(slider);

  // tick labels under the track, one per stop
  const ticks = d.createElement('div');
  ticks.style.cssText = `display:grid;grid-template-columns:repeat(${String(DETAIL_LEVELS.length)},1fr);gap:4px;margin-bottom:6px`;
  const tickEls = DETAIL_LEVELS.map((level, i) => {
    const t = d.createElement('button');
    t.type = 'button';
    t.dataset['graphicsTick'] = level;
    // the labels are also the control: a slider is fiddly with a trackpad, and the names
    // are the thing people actually aim at
    t.style.cssText =
      'font:600 11px/1.2 inherit;padding:3px 2px;border:0;background:none;cursor:pointer;color:inherit;' +
      (i === 0 ? 'text-align:left' : i === DETAIL_LEVELS.length - 1 ? 'text-align:right' : 'text-align:center');
    t.textContent = DETAIL[level].label;
    t.addEventListener('click', () => set({ ...state, detail: level }));
    ticks.append(t);
    return t;
  });
  panel.append(ticks);

  const note = d.createElement('p');
  note.dataset['graphicsNote'] = '1';
  note.style.cssText = 'margin:2px 0 4px;font-size:12px;opacity:.72;line-height:1.45;min-height:2.6em';
  panel.append(note);

  // D191: a tier change rebuilds a board in play, camera carried across. (The note beside it
  // that warned of a missing `pnpm gen-lods` went too: D214 tracks every tier's files, so no
  // shipped build can be without them.)
  const applyNote = d.createElement('p');
  applyNote.dataset['graphicsApply'] = '1';
  applyNote.textContent = 'Applies straight away — a board already in play is rebuilt in place, keeping your view.';
  applyNote.style.cssText = 'margin:0 0 4px;font-size:11.5px;opacity:.6;line-height:1.4';
  panel.append(applyNote);

  // ── the fps counter: a PLAYER control, so it sits with Graphics ──────────────
  // It applies without a rebuild (the stage re-reads it every sampling window), unlike the
  // tier above, which has to build the board again to change it.
  const fpsBtn = toggle('Show FPS', 'A small frame-rate counter on the board. Applies straight away, mid-game included.', 'fps');

  // ── D249: the living board. A player control, and the second one that applies live ──
  const lifeBtn = toggle(
    'Living board',
    'Sheep look over and wander towards your pointer; wheat and trees stir where you drag. Off leaves them grazing and still. Applies straight away.',
    'liveBoard',
  );

  // ── Sound (D210): the SAME master mute the menu bar and break menu toggle ────
  let unsubMute: (() => void) | null = null;
  if (opts.audio) {
    const audio = opts.audio;
    const row = d.createElement('div');
    row.style.cssText = ROW;
    const text = d.createElement('div');
    const t = d.createElement('div');
    t.textContent = 'Sound';
    t.style.cssText = 'font-size:14px;font-weight:600';
    const hint = d.createElement('div');
    hint.textContent = 'One master switch — the same one on the main menu and in the pause menu.';
    hint.style.cssText = 'font-size:11.5px;opacity:.65;line-height:1.4;max-width:280px';
    text.append(t, hint);
    const soundBtn = d.createElement('button');
    soundBtn.type = 'button';
    soundBtn.dataset['settingsMute'] = '1';
    soundBtn.style.cssText =
      'min-width:64px;padding:7px 14px;border-radius:999px;cursor:pointer;font:inherit;font-weight:700;' +
      'border:2px solid var(--hud-border,#2b2019);background:transparent;color:inherit';
    const paintMute = (): void => {
      const muted = audio.isMuted();
      soundBtn.textContent = muted ? 'Off' : 'On';
      soundBtn.style.background = muted ? 'transparent' : 'var(--hud-green,#3d8a58)';
      soundBtn.style.color = muted ? 'inherit' : 'var(--hud-cream,#f7f1e3)';
      // aria-pressed describes "sound is on" here — the row label is Sound, not Mute
      soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
      soundBtn.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
    };
    soundBtn.addEventListener('click', () => {
      audio.setMuted(!audio.isMuted());
      paintMute();
    });
    unsubMute = audio.onMuteChange(paintMute); // a toggle elsewhere moves this one too
    paintMute();
    row.append(text, soundBtn);
    panel.append(row);
  }

  // ── Notifications (D215): your turn + affordable offers, while the tab is hidden ───
  if (opts.notify && opts.notify.state() !== 'unsupported') {
    const notify = opts.notify;
    const row = d.createElement('div');
    row.style.cssText = ROW;
    const text = d.createElement('div');
    const t = d.createElement('div');
    t.textContent = 'Notifications';
    t.style.cssText = 'font-size:14px;font-weight:600';
    const hint = d.createElement('div');
    hint.style.cssText = 'font-size:11.5px;opacity:.65;line-height:1.4;max-width:280px';
    text.append(t, hint);
    const btn = d.createElement('button');
    btn.type = 'button';
    btn.dataset['settingsNotify'] = '1';
    btn.style.cssText =
      'min-width:64px;padding:7px 14px;border-radius:999px;cursor:pointer;font:inherit;font-weight:700;' +
      'border:2px solid var(--hud-border,#2b2019);background:transparent;color:inherit';
    const paint = (): void => {
      const st = notify.state();
      if (st === 'denied') {
        hint.textContent = 'Blocked by the browser — allow notifications for this site to turn them on.';
        btn.textContent = 'Blocked';
        btn.disabled = true;
        btn.style.opacity = '0.55';
        return;
      }
      hint.textContent = 'Your turn, and trade offers you can afford — only while this tab is in the background.';
      btn.disabled = false;
      btn.style.opacity = '1';
      if (st === 'default') {
        btn.textContent = 'Enable';
        btn.style.background = 'transparent';
        btn.style.color = 'inherit';
        btn.setAttribute('aria-pressed', 'false');
        return;
      }
      const on = notify.get();
      btn.textContent = on ? 'On' : 'Off';
      btn.style.background = on ? 'var(--hud-green,#3d8a58)' : 'transparent';
      btn.style.color = on ? 'var(--hud-cream,#f7f1e3)' : 'inherit';
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    };
    btn.addEventListener('click', () => {
      const st = notify.state();
      if (st === 'default') {
        void notify.request().then(paint); // the click IS the user gesture the browser wants
        return;
      }
      if (st === 'granted') {
        notify.set(!notify.get());
        paint();
      }
    });
    paint();
    row.append(text, btn);
    panel.append(row);
  }

  // ── Privacy (D212): the anonymous-usage toggle, beside a link to the words ───
  if (opts.usage) {
    const usage = opts.usage;
    const row = d.createElement('div');
    row.style.cssText = ROW;
    const text = d.createElement('div');
    const t = d.createElement('div');
    t.textContent = 'Share anonymous usage data';
    t.style.cssText = 'font-size:14px;font-weight:600';
    const hint = d.createElement('div');
    hint.style.cssText = 'font-size:11.5px;opacity:.65;line-height:1.4;max-width:280px';
    hint.textContent = 'First-party only — languages, country, performance, feature use. ';
    const link = d.createElement('a');
    link.href = '/legal/privacy.html';
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = 'Privacy policy';
    link.style.cssText = 'color:inherit;text-decoration:underline';
    hint.append(link);
    text.append(t, hint);
    const btn = d.createElement('button');
    btn.type = 'button';
    btn.dataset['settingsUsage'] = '1';
    btn.style.cssText =
      'min-width:64px;padding:7px 14px;border-radius:999px;cursor:pointer;font:inherit;font-weight:700;' +
      'border:2px solid var(--hud-border,#2b2019);background:transparent;color:inherit';
    const paint = (): void => {
      const on = usage.get();
      btn.textContent = on ? 'On' : 'Off';
      btn.style.background = on ? 'var(--hud-green,#3d8a58)' : 'transparent';
      btn.style.color = on ? 'var(--hud-cream,#f7f1e3)' : 'inherit';
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    };
    btn.addEventListener('click', () => {
      usage.set(!usage.get());
      paint();
    });
    paint();
    row.append(text, btn);
    panel.append(row);
  }

  // ── developer A/Bs, subordinate on purpose ───────────────────────────────────
  const devHead = d.createElement('div');
  devHead.textContent = 'Developer';
  devHead.style.cssText = 'margin-top:18px;font:600 10.5px/1 inherit;letter-spacing:.12em;text-transform:uppercase;opacity:.5';
  panel.append(devHead);

  const propsBtn = toggle('Scatter props', 'Off removes all 531 of them — the fastest way to see what the decoration costs.', 'props');
  const statsBtn = toggle('Render stats', 'Live draw calls, triangles and frame time over the board.', 'stats');

  const foot = d.createElement('div');
  foot.style.cssText = 'display:flex;justify-content:flex-end;gap:10px;margin-top:16px';
  const close = d.createElement('button');
  close.type = 'button';
  close.textContent = 'Done';
  close.dataset['settingsClose'] = '1';
  close.style.cssText =
    'padding:10px 22px;border-radius:999px;cursor:pointer;font:inherit;font-weight:700;' +
    'border:2px solid var(--hud-green-border,#2f6b45);background:var(--hud-green,#3d8a58);color:var(--hud-cream,#f7f1e3)';
  close.addEventListener('click', () => api.close());
  foot.append(close);
  panel.append(foot);

  // ── state ────────────────────────────────────────────────────────────────────
  let state: RenderSettings = opts.read();

  const paint = (): void => {
    const spec = DETAIL[state.detail];
    slider.value = String(detailIndex(state.detail));
    slider.setAttribute('aria-valuetext', spec.label);
    note.textContent = `${spec.label} — scenery ×${String(spec.propRatio)}, harbour ×${String(spec.portRatio)}, sheep ×${String(spec.flockRatio)}, shadows ${spec.shadows ? 'on' : 'off'}. ${spec.note}`;
    for (const [i, t] of tickEls.entries()) {
      const on = i === detailIndex(state.detail);
      t.style.opacity = on ? '1' : '.5';
      t.style.fontWeight = on ? '800' : '600';
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    const flag = (b: HTMLButtonElement, on: boolean): void => {
      b.textContent = on ? 'On' : 'Off';
      b.style.background = on ? 'var(--hud-green,#3d8a58)' : 'transparent';
      b.style.color = on ? 'var(--hud-cream,#f7f1e3)' : 'inherit';
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    };
    flag(fpsBtn, state.fps);
    flag(lifeBtn, state.liveBoard);
    flag(propsBtn, state.props);
    flag(statsBtn, state.stats);
  };

  const set = (next: RenderSettings): void => {
    state = next;
    opts.write(next);
    paint();
  };

  slider.addEventListener('input', () => set({ ...state, detail: detailAt(Number(slider.value)) }));

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && root.style.display !== 'none') {
      e.preventDefault();
      api.close();
    }
  };
  d.addEventListener('keydown', onKey);
  root.addEventListener('click', (e) => {
    if (e.target === root) api.close(); // the scrim closes it; the panel does not
  });

  d.body.appendChild(root);
  paint();

  const api: SettingsPanel = {
    root,
    open(): void {
      state = opts.read(); // never show a tier the URL or another tab has since changed
      paint();
      root.style.display = 'flex';
    },
    close(): void {
      root.style.display = 'none';
    },
    isOpen(): boolean {
      return root.style.display !== 'none';
    },
    dispose(): void {
      unsubMute?.();
      d.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
  return api;
}
